import { css } from '@emotion/core'
import { GatsbyImage } from 'gatsby-plugin-image'
import parse from 'html-react-parser'
import * as React from 'react'

import Image from './Image'
import { Link } from './Link'
import SvgIcon from './SvgIcon'
import VerticalTextDivider from './VerticalTextDivider'

export interface TextCardContentProps {
  title?: string
  blueSubtitle?: string
  graySubtitle?: string
  description: string
  footnote?: string
  iconSrc?: string
  imageSrc?: string
  gatsbyImageSrc?: any
  hoverImageSrc?: string
  roundedImage?: boolean
  imgWithBorder?: boolean
  isVertical?: boolean
  linkText?: string
  arrowLinkUrl?: string
  additionalJsx?: React.ReactNode
  isCentered?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  iconAboveImageSrc?: string
  withImageOverlay?: boolean
  clickable: boolean
  headingClassName?: string
  imgStyle?: any
  withoutBorderRadius?: boolean
}

const TextCardContent: React.FunctionComponent<TextCardContentProps> = ({
  title,
  blueSubtitle,
  graySubtitle,
  description,
  footnote,
  iconSrc,
  imageSrc,
  imgStyle,
  gatsbyImageSrc,
  imgWithBorder = false,
  hoverImageSrc,
  roundedImage = false,
  linkText,
  arrowLinkUrl,
  additionalJsx,
  isCentered = false,
  onClick,
  iconAboveImageSrc,
  withImageOverlay,
  clickable,
  headingClassName,
  withoutBorderRadius = false,
}) => {
  return (
    <div css={[flexContainerStyle(clickable)]}>
      {iconSrc && (
        <div
          id="text-card-image"
          css={imageContainerStyle(imgWithBorder, isCentered, clickable, withoutBorderRadius, hoverImageSrc)}
        >
          <SvgIcon src={iconSrc} />
        </div>
      )}

      {(imageSrc || gatsbyImageSrc) && (
        <div
          id="text-card-image"
          css={imageContainerStyle(imgWithBorder, isCentered, clickable, withoutBorderRadius, hoverImageSrc)}
        >
          {gatsbyImageSrc && (
            <GatsbyImage
              image={gatsbyImageSrc.gatsbyImageData}
              alt={title ? title : description}
              css={imageStyle(roundedImage, isCentered, imgStyle, imageSrc)}
              imgStyle={imgStyle}
            />
          )}
          {imageSrc && (
            <Image
              src={imageSrc}
              alt={title ? title : description}
              css={imageStyle(roundedImage, isCentered, imgStyle, imageSrc)}
              imgStyle={imgStyle}
            />
          )}

          {hoverImageSrc && (
            <div css={hoverImageStyle}>
              <Image
                src={hoverImageSrc}
                alt={title ? title : description}
                css={imageStyle(roundedImage, isCentered, imgStyle, imageSrc)}
                imgStyle={imgStyle}
                placeholder="dominantColor"
              />
            </div>
          )}

          {iconAboveImageSrc && <SvgIcon id="icon-above-image" src={iconAboveImageSrc} />}
          {withImageOverlay ? <div css={imageOverlayStyle} onClick={onClick} /> : undefined}
        </div>
      )}

      <div id="text-card-content" css={contentStyle(clickable, !!footnote, !!linkText, withoutBorderRadius)}>
        <div>
          {additionalJsx && additionalJsx}

          {(graySubtitle || blueSubtitle) && (
            <div className="gray-text strong-body-2">
              {blueSubtitle && (
                <p className="blue-text strong-body-2" css={subtitleStyle}>
                  {blueSubtitle}
                  {graySubtitle && blueSubtitle && <VerticalTextDivider />}
                </p>
              )}
              {graySubtitle}
            </div>
          )}

          {title && <p className={`${headingClassName} black-text ${isCentered && 'center'}`}>{parse(title)}</p>}

          {footnote && <div className="footnote">{footnote}</div>}

          <div className={`body-1 black-text ${isCentered && 'center'}`}>{parse(description)}</div>
        </div>

        {linkText && (
          <Link
            href={arrowLinkUrl}
            withArrow
            className="strong-body-2 blue-gradient-text opacity-on-hover"
            css={linkStyle}
          >
            {linkText}
          </Link>
        )}
      </div>
    </div>
  )
}

export default TextCardContent

const flexContainerStyle = (clickable: boolean) => css`
  display: flex;
  flex-direction: column;
  ${clickable && 'height: 100%;'};
`

const imageContainerStyle = (
  imgWithBorder: boolean,
  isCentered: boolean,
  clickable: boolean,
  withoutBorderRadius: boolean,
  hoverImageSrc?: string
) => css`
  position: relative;
  max-height: 230px;
  border-radius: ${withoutBorderRadius ? 0 : clickable ? '0.25rem 0.25rem' : '0.5rem 0.5rem'} 0 0;
  overflow: hidden;
  z-index: 0;

  ${isCentered && hoverImageSrc && 'margin: auto'};
  ${imgWithBorder &&
  `
    border: 1px solid #efeff0;
    border-radius: ${withoutBorderRadius ? 0 : clickable ? '0.25rem 0.25rem' : '0.5rem 0.5rem'} 0 0;
    border-bottom: none;
    overflow: hidden;
    
    `};
`

const imageStyle = (roundedImage: boolean, isCentered: boolean, imgStyle?: any, imageSrc?: string) => css`
  width: ${imgStyle?.width ? imgStyle.width : '100%'};
  height: ${imgStyle?.height ? imgStyle.height : '100%'};
  max-width: ${imgStyle?.maxWidth ? imgStyle.maxWidth : 'auto'};
  max-height: ${imgStyle?.maxHeight ? imgStyle.maxHeight : 'auto'};
  ${roundedImage && 'border-radius: 50%; width: 11rem; height: 11rem;'};
  ${isCentered && imageSrc && 'img { margin: auto }'};
`

const hoverImageStyle = css`
  background: #fff; //to mask any appearance of the img below
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transition: 0.2s opacity ease-in-out;

  &:hover {
    opacity: 1;
  }
`

// export for VideoPopup.tsx
export const imageOverlayStyle = css`
  position: absolute;
  background: #0a1c33;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.4;
  z-index: 1;
`

const contentStyle = (
  clickable: boolean,
  withFootnote: boolean,
  withLinkText: boolean,
  withoutBorderRadius: boolean
) => css`
  .gray-text {
    margin-bottom: 0.75rem;
  }

  .heading4 {
    margin-bottom: ${withFootnote ? '0' : '0.5rem'};
  }

  .footnote {
    margin-bottom: 0.25rem;
  }

  .body-1 {
    margin: 0 0 ${withLinkText ? '0.75rem' : '0'};
  }

  ${clickable
    ? `
    border: 1px solid #efeff0;
    border-top: none;
    padding: 1.5rem;
    border-radius: ${withoutBorderRadius ? 0 : '0 0 0.25rem 0.25rem'};
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `
    : 'padding-top: 1rem;'};
`

const subtitleStyle = css`
  display: inline-block;
  margin: 0;
`

const linkStyle = css`
  width: fit-content;
`
